import { useEffect, useState } from "react";
import { Download, Check, WifiOff } from "lucide-react";

/** Hook to track service-worker installation status and online state. */
export function useInstallStatus() {
  const [supported] = useState(typeof window !== "undefined" && "serviceWorker" in navigator);
  const [installed, setInstalled] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg && (reg.active || reg.installing || reg.waiting)) setInstalled(true);
    });
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [supported]);

  return { supported, installed, online };
}

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    // If already in standalone, hide button
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  if (!deferredPrompt) return null;

  const onClick = async () => {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <button
      onClick={onClick}
      data-testid="pwa-install-btn"
      className="bg-[#FFD700] text-zinc-950 border-2 border-zinc-950 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1.5 hover:neo-shadow hover:-translate-y-0.5 transition-all"
    >
      <Download size={12} strokeWidth={2.75} /> Install App
    </button>
  );
};

export const OfflineBadge = () => {
  const { online, installed } = useInstallStatus();
  if (online && !installed) return null;
  return (
    <div
      data-testid={online ? "pwa-ready-badge" : "pwa-offline-badge"}
      className={`hidden md:inline-flex items-center gap-1.5 border-2 border-zinc-950 px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${
        online ? "bg-[#00E5FF] text-zinc-950" : "bg-[#FF003C] text-white"
      }`}
      title={online ? "App cached for offline use" : "You are offline — using cached data"}
    >
      {online ? <Check size={10} strokeWidth={3} /> : <WifiOff size={10} strokeWidth={3} />}
      {online ? "Offline-Ready" : "Offline Mode"}
    </div>
  );
};
